import { forwardRef } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";
import { getCharIcon } from "../../utils/characteristicIcons";
import {
  characteristicLabel,
  isAffirmative,
  isBooleanCharacteristic,
  isNumericCharacteristic,
} from "../../utils/characteristicLabel";

// Cartel horizontal (16:9, 1920x1080) pensado para pasar en una pantalla/TV
// de la oficina: foto a pantalla completa con un degradé abajo y los datos
// superpuestos en blanco, con tipografía grande para verse bien de lejos.
const CartelCardTV = forwardRef(function CartelCardTV(
  { property, image, selectedCharacteristics, qrUrl, id },
  ref
) {
  const badges = selectedCharacteristics.filter(
    ({ key, value }) =>
      isNumericCharacteristic(key) || (isBooleanCharacteristic(key) && isAffirmative(value))
  );

  return (
    <div
      ref={ref}
      id={id}
      style={{ width: "1920px", height: "1080px" }}
      className="relative bg-gray-900 overflow-hidden"
    >
      {image ? (
        <img
          src={image}
          alt={property.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-gray-800" />
      )}

      {/* Degradé para que el texto se lea sobre la foto */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />

      {property.state && (
        <span className="absolute top-14 left-16 bg-primary text-white text-2xl font-bold uppercase tracking-[0.18em] px-7 py-3 rounded-full shadow-lg">
          {property.state}
        </span>
      )}

      {/* Datos, superpuestos abajo */}
      <div className="absolute bottom-0 inset-x-0 px-16 pb-14 flex items-end justify-between gap-16 text-white">
        <div className="min-w-0">
          {(property.type || property.country) && (
            <p className="text-xl font-semibold uppercase tracking-[0.25em] text-white/70 mb-3">
              {[property.type, property.country].filter(Boolean).join("  ·  ")}
            </p>
          )}
          <h1 className="text-8xl font-black leading-[1.05] tracking-tight capitalize">
            {property.name}
          </h1>
          {(property.address || property.country) && (
            <p className="flex items-center gap-3 text-2xl text-white/80 mt-5">
              <FaMapMarkerAlt className="flex-shrink-0 w-7 h-7" />
              {[property.address, property.country].filter(Boolean).join(", ")}
            </p>
          )}

          {property.currency && property.currency !== "Consultar" && property.price && (
            <p className="text-6xl font-extrabold leading-none mt-8">
              <span className="text-2xl font-bold align-top mr-3">{property.currency}</span>
              {Number(property.price).toLocaleString("es-AR")}
            </p>
          )}

          {badges.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-14 gap-y-6 mt-10">
              {badges.map(({ key, value }) => {
                const Icon = getCharIcon(key);
                const numeric = isNumericCharacteristic(key);
                return (
                  <div key={key} className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    {numeric ? (
                      <div className="leading-tight">
                        <p className="text-4xl font-bold">{value}</p>
                        <p className="text-base text-white/70 uppercase tracking-wide">
                          {characteristicLabel(key)}
                        </p>
                      </div>
                    ) : (
                      <p className="text-2xl font-semibold">{characteristicLabel(key)}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-center flex-shrink-0 bg-white rounded-3xl p-6">
          <QRCodeSVG value={qrUrl} size={190} fgColor="#1f2937" />
          <p className="text-base font-semibold text-primary uppercase tracking-wide mt-3">
            Escaneá
          </p>
        </div>
      </div>
    </div>
  );
});

export default CartelCardTV;
